import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

// Order matters: launcher's index.css locks html/body to fullscreen + no
// scroll (correct for an on-device kiosk). The marketing build's demo/styles.css
// loads after it (imported by the demo components) so its scroll-enabling rules
// win — the marketing page IS scrollable.
import './styles/index.css';
import { App } from './App.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';

/**
 * Kiosk vs marketing mode.
 *
 * Same package, two builds, controlled by `VITE_KIOSK_MODE`:
 *   - `true`   → bare <App />. On-device launcher. Deployed to
 *                app.mobilauncher.com where MobiControl Lockdown points.
 *   - falsey   → marketing site (DemoShell at `/`, FeaturesPage at `/features`,
 *                DocsApp at `/docs/*`). Deployed to www / apex.
 *
 * The marketing entry is loaded via a **dynamic import** below: because
 * `KIOSK_MODE` is a build-time constant, the `else` branch (and the entire
 * `./demo/MarketingRoot` chunk it pulls in — MUI, emotion, every marketing
 * component) is dead-code-eliminated from the kiosk `build:bundle`. That keeps
 * the on-device launcher MUI-free and small.
 *
 * /docs + /features routing relies on the public/_redirects SPA fallback so any
 * such request hits index.html and the marketing root handles the path.
 */
const KIOSK_MODE: boolean = import.meta.env['VITE_KIOSK_MODE'] === 'true';

// Page-chrome lockdown for the kiosk build. Sets `body.kiosk-mode` before React
// mounts so the launcher's `styles/index.css` rules that lock html/body to
// 100% × 100% (no scroll) scope to this class only. The marketing build leaves
// body bare and the page scrolls normally.
if (KIOSK_MODE) {
  document.body.classList.add('kiosk-mode');
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('expected #root element in index.html');

if (KIOSK_MODE) {
  createRoot(rootEl).render(
    <StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </StrictMode>,
  );
} else {
  void import('./demo/MarketingRoot.js').then(({ mountMarketing }) => mountMarketing(rootEl));
}
